import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Bell, X, Check, AlertTriangle, Sparkles, Download, CreditCard, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  low_credits: { icon: AlertTriangle, color: '#EF4444', bg: '#EF4444' },
  plugin_update: { icon: Download, color: '#10B981', bg: '#10B981' },
  seo_complete: { icon: Sparkles, color: '#F59E0B', bg: '#F59E0B' },
  system: { icon: Bell, color: '#0055FF', bg: '#0055FF' },
  purchase: { icon: CreditCard, color: '#10B981', bg: '#10B981' }
};

const NotificationCenter = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('notif.now');
    if (mins < 60) return t('notif.minAgo', { n: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('notif.hourAgo', { n: hours });
    const days = Math.floor(hours / 24);
    return t('notif.dayAgo', { n: days });
  };
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/notifications`, authHeaders);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markRead = async (id) => {
    try {
      await axios.post(`${API_URL}/api/notifications/read/${id}`, {}, authHeaders);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, authHeaders);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const dismiss = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${id}`, authHeaders);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => {
        const n = notifications.find(n => n.id === id);
        return n && !n.read ? Math.max(0, prev - 1) : prev;
      });
    } catch {}
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-center">
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-white/5 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-[#A1A1AA]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-[#111111] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden z-50" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <h3 className="font-['Outfit'] font-medium text-sm">{t('notif.title')}</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-[10px] text-[#0055FF] hover:text-[#3377FF]" data-testid="mark-all-read-btn">
                {t('notif.markAllRead')}
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-8 h-8 text-[#A1A1AA]/30 mx-auto mb-2" />
                <p className="text-xs text-[#A1A1AA]">{t('notif.empty')}</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const iconCfg = ICON_MAP[notif.type] || ICON_MAP.system;
                const Icon = iconCfg.icon;
                return (
                  <div
                    key={notif.id}
                    className={`flex gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/3 transition-colors ${!notif.read ? 'bg-[#0055FF]/5' : ''}`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: iconCfg.bg + '15' }}>
                        <Icon className="w-4 h-4" style={{ color: iconCfg.color }} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-xs font-medium truncate ${!notif.read ? 'text-white' : 'text-[#A1A1AA]'}`}>
                          {notif.title}
                        </p>
                        <button onClick={() => dismiss(notif.id)} className="text-[#A1A1AA] hover:text-white flex-shrink-0">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-[10px] text-[#A1A1AA] mt-0.5 line-clamp-2">{notif.message}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10px] text-[#A1A1AA]/60">{timeAgo(notif.created_at)}</span>
                        <div className="flex items-center gap-2">
                          {notif.link && (
                            <a href={notif.link} className="text-[10px] text-[#0055FF] flex items-center gap-0.5 hover:underline">
                              {t('notif.viewLink')} <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          )}
                          {!notif.read && (
                            <button onClick={() => markRead(notif.id)} className="text-[10px] text-[#10B981] flex items-center gap-0.5">
                              <Check className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
